import { createMemo, Show } from "solid-js";
import { chronologie, cooccurrenceLeviers, distributionLeviers } from "../domaine/analyses";
import { etatTemporel, LIBELLES_ETAT } from "../domaine/portefeuille";
import { tableauDepuisMatrice } from "../export";
import { formaterScore, pluriel } from "../format";
import type { Analyse } from "./analyse";
import { Barres } from "./Barres";
import { Chronologie } from "./Chronologie";
import { Figure } from "./Figure";
import { Matrice } from "./Matrice";

/**
 * Ce que la donnée dit d'elle-même : quels leviers vont ensemble et comment les
 * projets se répartissent.
 */
export function VueExploration(props: { analyse: Analyse }) {
  const p = () => props.analyse.portefeuille;
  const total = () => p().projets.length;

  const co = createMemo(() =>
    cooccurrenceLeviers(p().projets, p().leviers, props.analyse.ordres.leviers),
  );
  const jaccard = (i: number, j: number) => {
    const a = co().ensembles[i];
    const b = co().ensembles[j];
    if (!a || !b || i === j) return null;
    let communs = 0;
    a.forEach((x) => {
      if (b.has(x)) communs++;
    });
    const union = a.size + b.size - communs;
    return { communs, union, valeur: union ? communs / union : 0 };
  };
  const libelleCo = (i: number) => p().leviers[co().ordre[i] ?? -1]?.libelle ?? "";

  const distribution = createMemo(() => distributionLeviers(p().projets, p().leviers));

  let svgCo: SVGSVGElement | undefined;

  return (
    <>
      <Show when={p().dimensions.leviers && co().ordre.length > 1}>
        <Figure
          id="co-occurrence-leviers"
          titre="Quelles familles de leviers vont ensemble"
          lecture={
            <>
              Indice de Jaccard : part des projets qui mobilisent les deux familles parmi ceux qui
              en mobilisent au moins une.
              <Show when={co().ecartes.length}>
                {" "}
                {co()
                  .ecartes.map((l) => `« ${l.libelle} »`)
                  .join(", ")}{" "}
                {co().ecartes.length > 1
                  ? "exclus (indicateurs dérivés)"
                  : "exclu (indicateur dérivé)"}
                .
              </Show>
            </>
          }
          svg={() => svgCo}
          tableau={() =>
            tableauDepuisMatrice(
              co().ordre.map((_, i) => libelleCo(i)),
              co().ordre.map((_, i) => libelleCo(i)),
              // eslint-disable-next-line solid/reactivity -- appelé au clic d'export, pas au rendu
              (i, j) => (j > i ? null : (jaccard(i, j)?.valeur ?? null)),
              (v) => v.toFixed(4),
            )
          }
        >
          <Matrice
            titre="Co-occurrence des familles de leviers"
            type="sequentiel"
            diagonale
            hauteurCible={150 + p().leviers.length * 58}
            lignes={co().ordre.map((k) => ({
              libelle: p().leviers[k]?.libelle ?? "",
              titre: p().leviers[k]?.libelleLong,
            }))}
            colonnes={co().ordre.map((k) => ({
              libelle: p().leviers[k]?.libelle ?? "",
              titre: p().leviers[k]?.libelleLong,
            }))}
            valeur={(i, j) => jaccard(i, j)?.valeur ?? null}
            masquer={(i, j) => j > i}
            infobulle={(i, j) => {
              const r = jaccard(i, j);
              return r
                ? `${libelleCo(i)} + ${libelleCo(j)} : ${r.communs} projets sur ${r.union} · Jaccard ${formaterScore(r.valeur)}`
                : "";
            }}
            refSvg={(e) => (svgCo = e)}
          />
          <p class="explication discret sous-vue">Co-occurrence, pas causalité.</p>
        </Figure>
      </Show>

      <Show when={p().dimensions.leviers}>
        <Figure
          id="distribution-leviers"
          titre="Combien de familles de leviers par projet"
          tableau={() => ({
            colonnes: ["nombre_de_leviers", "projets", "part"],
            lignes: distribution().map((n, k) => [k, n, total() ? (n / total()).toFixed(4) : ""]),
          })}
        >
          <Barres
            larges
            max={Math.max(1, ...distribution())}
            lignes={distribution().map((n, k) => ({
              libelle: `${k} famille${k > 1 ? "s" : ""}`,
              valeur: n,
              texte: `${n}${n && total() ? ` · ${Math.round((100 * n) / total())} %` : ""}`,
            }))}
          />
        </Figure>
      </Show>
    </>
  );
}

/** Comment le portefeuille se déploie dans le temps. */
export function VueChronologie(props: { analyse: Analyse }) {
  const p = () => props.analyse.portefeuille;
  const chrono = createMemo(() => chronologie(p().projets));
  let svgChrono: SVGSVGElement | undefined;

  return (
    <Show
      when={chrono().dates.length}
      fallback={
        <div class="entete-vue">
          <h2>Chronologie</h2>
          <p class="explication discret">Aucun projet n'a de date.</p>
        </div>
      }
    >
      <Figure
        id="chronologie"
        titre="Chronologie du portefeuille"
        lecture={
          chrono().sansDate.length
            ? `${pluriel(chrono().sansDate.length, "projet")} sans date, en bas.`
            : undefined
        }
        svg={() => svgChrono}
        tableau={() => ({
          colonnes: ["acronyme", "debut", "fin", "duree_mois", "etat"],
          lignes: p().projets.map((pr) => [
            pr.acronyme,
            pr.dateDebut ?? "",
            pr.dateFin ?? "",
            pr.dureeMois ?? "",
            LIBELLES_ETAT[etatTemporel(pr, props.analyse.aujourdhui)],
          ]),
        })}
      >
        <Chronologie
          donnees={chrono()}
          portefeuille={p()}
          aujourdhui={props.analyse.aujourdhui}
          surProjet={(ref) => props.analyse.selectionner(ref)}
          refSvg={(e) => (svgChrono = e)}
        />
      </Figure>
    </Show>
  );
}
